import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Card,
  CardContent,
  Alert,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Save as SaveIcon,
  Close as CloseIcon,
  LocalPharmacy as LocalPharmacyIcon,
  ListAlt as ListAltIcon,
} from '@mui/icons-material';
import { ProcedureService } from '../../services/procedureService';
import { createTimestamp } from '../../utils/firebaseUtils.js';
import PatientAllergiesService from '../../services/patientAllergiesService';
import MedicalHistoryIntegrationService from '../../services/medicalHistoryIntegrationService';
import { fetchPatientDetails } from '../../redux/slices/patientsSlice';
import { useEncounterData } from '../../hooks/useEncounterData';
import { useEncounterFormState } from '../../hooks/useEncounterFormState';
import { useEncounterTabs } from './EncounterTabs';
import AllergySelectorDialog from '../patients/AllergySelectorDialog';
import PrescriptionForm from '../prescriptions/PrescriptionForm';
import QuickListIntegrated from '../orders/QuickListIntegrated';

// Styled Components
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(1.5),
  borderRadius: 8,
  boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 6,
  textTransform: 'none',
  fontWeight: 600,
  padding: '6px 12px',
  fontSize: '0.875rem',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
}));

// Styled Dialog Components - Modern Theme
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  padding: theme.spacing(2, 3),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  '& .MuiTypography-root': {
    color: 'white',
  },
}));

const ModernButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(1, 3),
}));

/**
 * Utility function to sync encounter data to medical history
 * Call this after saving an encounter when status changes to completed/discharged
 * @param {Object} params - Parameters for syncing
 * @param {string} params.patientId - Patient ID
 * @param {string} params.facilityId - Facility ID
 * @param {Object} params.encounterData - Full encounter/form data
 * @param {string} params.encounterId - Encounter ID
 * @param {string} params.newStatus - New status of the encounter
 * @param {string} params.previousStatus - Previous status of the encounter (optional)
 * @param {string} params.facilityName - Facility name (optional)
 * @returns {Promise<Object>} Results of sync operations
 */
export const syncEncounterToMedicalHistory = async ({
  patientId,
  facilityId,
  encounterData,
  encounterId,
  newStatus,
  previousStatus = '',
  facilityName = ''
}) => {
  // Only sync when status changes to completed or discharged
  const shouldSync = (newStatus === 'completed' || newStatus === 'discharged') &&
                     previousStatus !== 'completed' && previousStatus !== 'discharged';
  
  if (!shouldSync) {
    console.log('EncounterBaseRefactored: Status does not require medical history sync');
    return null;
  }

  if (!patientId || !facilityId || !encounterData || !encounterId) {
    console.warn('EncounterBaseRefactored: Missing required parameters for medical history sync');
    return null;
  }

  try {
    console.log('EncounterBaseRefactored: Syncing encounter to medical history...', {
      patientId,
      encounterId,
      newStatus
    });

    const results = await MedicalHistoryIntegrationService.syncEncounterToMedicalHistory(
      patientId,
      facilityId,
      encounterData,
      encounterId,
      facilityName
    );

    console.log('EncounterBaseRefactored: Medical history sync completed', results);
    return results;
  } catch (error) {
    console.error('EncounterBaseRefactored: Error syncing to medical history:', error);
    // Don't throw - we don't want to fail the save if sync fails
    return { error: error.message };
  }
};

const EncounterBaseRefactored = ({
  mode = 'create',
  encounter = null,
  patient = null,
  prescriptions = [],
  recentEncounters = [],
  formData: externalFormData,
  setFormData: externalSetFormData,
  onSave,
  onBack,
  loading = false,
  saving = false,
  title = 'Encounter Details',
  subtitle = null,
  renderTaskList = null,
  showExtraTabs = false,
  facilityId = '',
  encounterType = null,
  onCareTeamChange,
  onSaveSuccess,
  customHeaderIcon = null,
  onRefreshEncounter = null,
  showPatientInfoInHeader = false,
}) => {

  const dispatch = useDispatch();
  const [mainTab, setMainTab] = useState(0);
  const [openPrescriptionDialog, setOpenPrescriptionDialog] = useState(false);
  const [openAllergiesDialog, setOpenAllergiesDialog] = useState(false);
  const [openQuickListDialog, setOpenQuickListDialog] = useState(false);

  // Use custom hooks for data management
  const encounterData = useEncounterData(encounter, patient, mode);
  const formState = useEncounterFormState(encounter, mode);
  
  // Use external form data if provided, otherwise use internal state
  const formData = externalFormData || formState.formData;
  const setFormData = externalSetFormData || formState.setFormData;
  const hasPendingChanges = formState.hasChanges;

  // Note: useProcedures is called inside useEncounterTabs - no need to call it here

  // Get tab definitions with lazy loading support
  const { tabDefs, markTabVisited } = useEncounterTabs({
    encounter,
    patient,
    prescriptions,
    formData,
    setFormData,
    mode,
    encounterType,
    facilityId,
    onCareTeamChange: (data) => {
      if (onCareTeamChange) onCareTeamChange(data);
    },
    onRefreshEncounter,
    renderTaskList,
    showExtraTabs,
    handleSendOrder: async (order) => {
      try {
        const procedureService = new ProcedureService();
        await procedureService.updateProcedure(order.id, { 
          status: 'Pending',
          sentDate: createTimestamp()
        });
        
        if (onRefreshEncounter) {
          onRefreshEncounter();
        }
        
        console.log('Order sent successfully:', order.description);
      } catch (error) {
        console.error('Error sending order:', error);
      }
    },
    encounterData,
    onOpenAllergiesDialog: () => setOpenAllergiesDialog(true),
    onOpenQuickListDialog: () => setOpenQuickListDialog(true),
    onOpenPrescriptionDialog: () => setOpenPrescriptionDialog(true),
    activeTabIndex: mainTab, // Pass current tab for lazy loading tracking
  });

  // Get location info for subtitle
  const locationInfo = (() => {
    const parts = [];
    if (encounter?.department) parts.push(`Department: ${encounter.department}`);
    if (encounter?.room) parts.push(`Room: ${encounter.room}`);
    if (encounter?.bed) parts.push(`Bed: ${encounter.bed}`);
    return parts.join(' | ');
  })();

  // Store the onSaveSuccess callback in a ref to avoid calling it during render
  const onSaveSuccessRef = useRef(onSaveSuccess);
  onSaveSuccessRef.current = onSaveSuccess;
  
  // Track if component has mounted
  const hasMountedRef = useRef(false);
  const resetFunctionRef = useRef(formState.resetChanges);
  resetFunctionRef.current = formState.resetChanges;

  // Provide reset function to parent - only call after component is fully mounted
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    
    // Use requestAnimationFrame to ensure this runs after the current render cycle
    const frameId = requestAnimationFrame(() => {
      // Only call the callback if the component is still mounted and the callback exists
      if (onSaveSuccessRef.current && hasMountedRef.current) {
        onSaveSuccessRef.current(resetFunctionRef.current);
      }
    });
    
    return () => cancelAnimationFrame(frameId);
  }, []);

  // Cleanup effect to reset mounted ref when component unmounts
  useEffect(() => {
    return () => {
      hasMountedRef.current = false;
    };
  }, []);

  const handleAllergiesUpdate = async (updatedAllergies) => {
    if (!patient?.id) {
      console.error('Cannot update allergies: No patient ID available');
      return;
    }
    
    try {
      const validAllergies = updatedAllergies.filter(allergy => allergy.name && allergy.name.trim());
      
      if (validAllergies.length === 0) {
        setOpenAllergiesDialog(false);
        return;
      }

      // Use the service to update allergies
      const patientAllergiesService = new PatientAllergiesService();
      await patientAllergiesService.updatePatientAllergies(patient.id, validAllergies);
      
      // Update patient data in Redux
      await dispatch(fetchPatientDetails(patient.id)).unwrap();
      
      // Update form data with new allergies
      if (formData) {
        setFormData({
          ...formData,
          allergies: validAllergies
        });
      }
      
      setOpenAllergiesDialog(false);
      
      // Show success message or update UI as needed
      console.log('Successfully updated patient allergies:', validAllergies);
    } catch (error) {
      console.error('Error saving allergies:', error);
      // Show error message to user
      // You might want to add a snackbar or alert component to show this
      throw new Error('Failed to update allergies. Please try again.');
    }
  };

  if (loading || !formData) {
    return (
      <Box sx={{ p: 2, minHeight: '100vh' }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography variant="h6" color="text.secondary">Loading encounter details...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      {/* Compact Header */}
      <HeaderCard>
        <CardContent sx={{ py: '12px !important' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {customHeaderIcon && customHeaderIcon}
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, fontSize: '1.5rem' }}>
                  {title}
                </Typography>
                {subtitle && (
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mb: 0.5 }}>
                    {subtitle}
                  </Typography>
                )}
                {locationInfo && (
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mb: 0.5 }}>
                    {locationInfo}
                  </Typography>
                )}
                {showPatientInfoInHeader && (
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', fontSize: '0.875rem' }}>
                    {[
                      patient?.name && patient.name,
                      patient?.mrn && (patient.mrn.startsWith('MRN') ? patient.mrn : `MRN: ${patient.mrn}`),
                      patient?.gender && patient.gender,
                      patient?.dateOfBirth && `DOB: ${new Date(patient.dateOfBirth).toLocaleDateString()}`,
                      encounter?.fin && (encounter.fin.startsWith('FIN') ? encounter.fin : `FIN: ${encounter.fin}`)
                    ].filter(Boolean).join(' | ')}
                  </Box>
                )}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {onBack && (
                <ActionButton variant="outlined" onClick={onBack} sx={{ color: 'white', borderColor: 'white' }}>
                  Back
                </ActionButton>
              )}
              {onSave && (
                <ActionButton
                  variant="contained"
                  color={hasPendingChanges ? "warning" : "primary"}
                  onClick={onSave}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                  sx={hasPendingChanges ? { backgroundColor: '#ff9800', '&:hover': { backgroundColor: '#f57c00' } } : {}}
                >
                  {saving ? 'Saving...' : mode === 'create' ? 'Create' : hasPendingChanges ? 'Save Changes' : 'Save'}
                </ActionButton>
              )}
            </Box>
          </Box>
        </CardContent>
      </HeaderCard>

      {/* Simplified Main Tabs */}
      <Box sx={{ 
        background: '#ffffff',
        borderRadius: 2,
        boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
        mb: 1
      }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={mainTab} 
            onChange={(_, v) => {
              // Only allow navigation to enabled tabs
              if (!tabDefs[v]?.disabled) {
                setMainTab(v);
                // Mark tab as visited for lazy loading
                if (markTabVisited) {
                  markTabVisited(v);
                }
              }
            }} 
            variant="scrollable"
            scrollButtons="auto"
            sx={{ 
              '& .MuiTab-root': { 
                minHeight: 32, 
                textTransform: 'none', 
                fontWeight: 600,
                fontSize: '0.875rem',
                px: 2,
                py: 1
              },
              '& .MuiTab-root.Mui-selected': {
                fontWeight: 800
              },
              '& .MuiTab-root.Mui-disabled': {
                opacity: 0.5,
              },
              '& .MuiTabs-flexContainer': {
                gap: 0
              }
            }}
          >
            {tabDefs.map((tab, idx) => (
              <Tab 
                key={typeof tab.label === 'string' ? tab.label : `tab-${idx}`} 
                label={tab.label} 
                disabled={tab.disabled}
              />
            ))}
          </Tabs>
        </Box>

        {/* Info message when encounter not yet created */}
        {mode === 'create' && !encounter?.id && (
          <Box sx={{ px: 2, pt: 2, pb: 0 }}>
            <Alert severity="info" sx={{ py: 0.5 }}>
              Please fill in the clinical data and click <strong>Create</strong> to save the encounter. Other tabs will become available after the encounter is created.
            </Alert>
          </Box>
        )}

        <Box sx={{ p: 2 }}>
          {tabDefs.map((tab, idx) => (
            mainTab === idx && (
              <React.Fragment key={typeof tab.label === 'string' ? tab.label : `tab-content-${idx}`}>
                {tab.render}
              </React.Fragment>
            )
          ))}
        </Box>
      </Box>

      {/* Dialogs */}
      <StyledDialog 
        open={openPrescriptionDialog} 
        onClose={() => setOpenPrescriptionDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh' }
        }}
      >
        <StyledDialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <LocalPharmacyIcon sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', lineHeight: 1.2 }}>
                Add Prescription
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.85rem' }}>
                Create a new prescription for this encounter
              </Typography>
            </Box>
          </Box>
          <IconButton 
            onClick={() => setOpenPrescriptionDialog(false)} 
            sx={{ 
              color: 'white',
              '&:hover': { 
                backgroundColor: 'rgba(255, 255, 255, 0.1)' 
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>
        <DialogContent sx={{ p: 3, bgcolor: '#f5f5f5' }}>
          <PrescriptionForm
            patientId={patient?.id || encounter?.patientId}
            encounterId={encounter?.id}
            onClose={() => setOpenPrescriptionDialog(false)}
          />
        </DialogContent>
      </StyledDialog>

      <StyledDialog 
        open={openQuickListDialog} 
        onClose={() => setOpenQuickListDialog(false)} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh' }
        }}
      >
        <StyledDialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ListAltIcon sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', lineHeight: 1.2 }}>
                Quick Order List
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.85rem' }}>
                Select orders from facility quick list
              </Typography>
            </Box>
          </Box>
          <IconButton 
            onClick={() => setOpenQuickListDialog(false)} 
            sx={{ 
              color: 'white',
              '&:hover': { 
                backgroundColor: 'rgba(255, 255, 255, 0.1)' 
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>
        <DialogContent sx={{ p: 3, bgcolor: '#f5f5f5' }}>
          <QuickListIntegrated
            patientId={patient?.id || encounter?.patientId}
            encounterId={encounter?.id}
            facilityId={facilityId || encounter?.facilityId || patient?.facilityId || ''}
            maxHeight="400px"
          />
        </DialogContent>
      </StyledDialog>

      {/* Allergies Dialog */}
      <AllergySelectorDialog
        open={openAllergiesDialog}
        onSave={handleAllergiesUpdate}
        onCancel={() => setOpenAllergiesDialog(false)}
        initialAllergies={patient?.allergies || []}
        patientName={patient?.name}
      />
    </Box>
  );
};

export default EncounterBaseRefactored; 